// The control that folds a surface away — and, just as importantly, unfolds it.
//
// WHY IT IS THIS BIG. The console's original collapse affordance was a 9px
// chevron tucked into the right-hand end of a header, and the report from the
// bridge was simply that nobody can find them. A control nobody finds is a
// control nobody has, so this one is sized to be seen: 18×54 on a vertical
// splitter, 54×18 on a horizontal edge, bordered and on its own plate rather
// than floating as a bare glyph.
//
// ONE DEFINITION, THREE SURFACES. The forecast column, the workspace panel and
// the conditions table all fold, and before this they folded by three different
// means at two different sizes. Whatever a person learns from folding one of
// them should carry to the others; that only holds if there is one control, so
// this module owns it and the callers own what folding means.
//
// THE CHEVRON POINTS THE WAY THE SURFACE WILL GO, and back at the space it came
// from once it is folded. Rotation is the caller's, through CSS, because only
// the caller knows which way "away" is.
//
// A FOLD MUST CARRY ITS OWN WAY BACK. Nothing here enforces that — it cannot —
// but it is the rule every caller has to satisfy: fold something with a control
// that then disappears and the surface is gone for good short of clearing
// storage. The right column keeps its splitter track at zero width so the grip
// survives; the conditions table keeps its header bar; the workspace panel is
// reopened by the rail.

export interface GripOptions {
  /** Tooltip and accessible name while the surface is OPEN (i.e. "hide X"). */
  hideLabel: string;
  /** Tooltip and accessible name while it is FOLDED (i.e. "show X"). */
  showLabel: string;
  /** Called on activation with the state being moved TO. */
  onToggle(folded: boolean): void;
  /** Whether the surface starts folded. */
  folded?: boolean;
}

export interface Grip {
  root: HTMLButtonElement;
  /** Re-label after the state changed elsewhere (a restored preference, say). */
  setFolded(folded: boolean): void;
}

export function createGrip(opts: GripOptions): Grip {
  const root = document.createElement("button");
  root.type = "button";
  root.className = "gutgrip";
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 12 12");
  svg.setAttribute("aria-hidden", "true");
  // Points left at rest; every placement rotates it from there.
  svg.innerHTML =
    '<path d="M7.5 2.5 4 6l3.5 3.5" fill="none" stroke="currentColor" ' +
    'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>';
  root.appendChild(svg);

  let folded = opts.folded ?? false;
  const label = (): void => {
    const t = folded ? opts.showLabel : opts.hideLabel;
    root.title = t;
    root.setAttribute("aria-label", t);
    root.setAttribute("aria-expanded", String(!folded));
  };
  label();

  // A grip often sits on a drag surface with pointer capture (the splitters).
  // Without this the press that folds the column starts a resize first, and the
  // column jumps on its way out.
  root.addEventListener("pointerdown", (e) => e.stopPropagation());
  root.addEventListener("dblclick", (e) => e.stopPropagation());
  root.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    folded = !folded;
    label();
    opts.onToggle(folded);
  });

  return {
    root,
    setFolded(next: boolean): void {
      folded = next;
      label();
    },
  };
}
